import LeadCaptureBox from "@/components/LeadCaptureBox";
import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SeoHead from "@/components/SeoHead";
import Spinner from "@/components/Spinner";
import { SITE_URL } from "@/lib/siteConfig";
import { sanitizeHtml } from "@/lib/sanitize";

const API = "/api/public";

export default function SeoPageView() {
  const { slug } = useParams<{ slug: string }>();
  const [page, setPage] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    fetch(`${API}/pages/${slug}`)
      .then((r) => { if (!r.ok) { setNotFound(true); return null; } return r.json(); })
      .then((data) => { if (data) setPage(data.page); })
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  if (loading) return <Spinner className="py-32" />;
  if (notFound || !page) return (
    <div className="max-w-4xl mx-auto px-6 py-32 text-center">
      <h1 className="text-2xl font-bold mb-2">Page not found</h1>
      <Link to="/" className="text-coral font-semibold hover:underline">← Back to home</Link>
    </div>
  );

  return (
    <>
      <SeoHead
        title={page.meta_title || page.title}
        description={page.meta_description || ""}
        ogImage={page.og_image}
        url={`${SITE_URL}/${page.slug}`}
        breadcrumbs={[
          { name: "Home", url: SITE_URL },
          { name: page.title, url: `${SITE_URL}/${page.slug}` },
        ]}
      />

      <div className="max-w-3xl mx-auto px-6 py-16">
        <h1 className="font-display text-4xl font-bold mb-8 leading-tight">{page.h1}</h1>

        <div
          className="prose prose-lg max-w-none
            [&_h2]:font-display [&_h2]:text-2xl [&_h2]:font-bold [&_h2]:mt-10 [&_h2]:mb-4
            [&_h3]:font-display [&_h3]:text-xl [&_h3]:font-bold [&_h3]:mt-8 [&_h3]:mb-3
            [&_p]:text-slate [&_p]:leading-relaxed [&_p]:mb-4
            [&_a]:text-coral [&_a]:font-semibold
            [&_ul]:list-disc [&_ul]:pl-6 [&_ul]:mb-4
            [&_li]:text-slate [&_li]:mb-1"
          dangerouslySetInnerHTML={{ __html: sanitizeHtml(page.content) }}
        />

        {/* CTA */}
        <div className="mt-12 bg-ink rounded-2xl p-8 text-center text-white">
          <h2 className="font-display text-2xl font-bold mb-2">Get started with MoreMail</h2>
          <p className="text-white/60 mb-6">AI-powered email marketing — free for up to 1,000 subscribers.</p>
          <div className="flex justify-center">
            <LeadCaptureBox />
          </div>
        </div>
      </div>
    </>
  );
}
